/**
 * Shared authorization for the public photo-download endpoints (view, zip,
 * single-photo). Centralises the checks the three routes used to duplicate, and
 * adds token expiry (#702): a download token is valid only while the purchase is
 * completed, belongs to the album in the URL, and is within its lifetime.
 */

/** How long a download token stays usable after purchase. */
export const DOWNLOAD_TOKEN_TTL_DAYS = 90;
const DAY_MS = 24 * 60 * 60 * 1000;

export interface DownloadPurchaseLike {
  /** null for a purchase made through the event's master link */
  albumId: string | null;
  /** set only for a master-link purchase; null for every album purchase */
  eventId?: string | null;
  status: string;
  createdAt: Date | string;
  /**
   * For a pre-purchase, when it was redeemed: the redeem routes stamp it as
   * they email the download link. Optional, so a caller that does not load it
   * falls back to createdAt.
   */
  notifiedAt?: Date | string | null;
}

export type DownloadDenyReason = 'not_found' | 'wrong_album' | 'wrong_event' | 'not_completed' | 'expired';

export interface DownloadAccessResult {
  ok: boolean;
  reason?: DownloadDenyReason;
}

function ms(d: Date | string | null | undefined): number {
  if (!d) return NaN;
  return typeof d === 'string' ? Date.parse(d) : d.getTime();
}

/**
 * When a token's lifetime starts: the purchase, or, for a pre-purchase, the
 * redemption. A pre-purchase is paid at registration, often months before the
 * photos exist, so its createdAt is the registration date; measured from that,
 * a token issued on redemption could be dead the moment it was issued (any
 * pre-purchase made more than DOWNLOAD_TOKEN_TTL_DAYS before race day). The
 * redeem routes stamp notifiedAt when they email the download link, which is
 * when this token was issued.
 */
export function tokenIssuedAt(purchase: Pick<DownloadPurchaseLike, 'createdAt' | 'notifiedAt'>): number {
  const created = ms(purchase.createdAt);
  const redeemed = ms(purchase.notifiedAt);
  if (Number.isFinite(redeemed) && (!Number.isFinite(created) || redeemed > created)) return redeemed;
  return created;
}

function isExpired(purchase: DownloadPurchaseLike, now: number): boolean {
  const issued = tokenIssuedAt(purchase);
  return Number.isFinite(issued) && now - issued > DOWNLOAD_TOKEN_TTL_DAYS * DAY_MS;
}

/**
 * Which links a purchase works through, which is what its download token can
 * be spent on: 'album' when it names an album (that album's link), 'event'
 * when it names only the event (the event's master link). A row with both
 * (split-pay writes some) is album-scoped here and also passes the event check.
 */
export function purchaseScope(purchase: Pick<DownloadPurchaseLike, 'albumId' | 'eventId'>): 'album' | 'event' | null {
  if (purchase.albumId) return 'album';
  if (purchase.eventId) return 'event';
  return null;
}

/**
 * Decide whether a download token's purchase may be used for `albumId` right
 * now. Pure and time-injected so it is unit-testable.
 */
export function checkDownloadAccess(
  purchase: DownloadPurchaseLike | null | undefined,
  albumId: string,
  now: number = Date.now(),
): DownloadAccessResult {
  if (!purchase) return { ok: false, reason: 'not_found' };
  if (purchase.albumId !== albumId) return { ok: false, reason: 'wrong_album' };
  if (purchase.status !== 'completed') return { ok: false, reason: 'not_completed' };
  if (isExpired(purchase, now)) return { ok: false, reason: 'expired' };
  return { ok: true };
}

/**
 * The master-link equivalent: the purchase must be event-scoped and belong to
 * THIS event. An album purchase (eventId null) is rejected here, and an event
 * purchase (albumId null) is rejected by checkDownloadAccess above, so a token
 * minted for one scope can never be spent through the other's endpoints.
 */
export function checkEventDownloadAccess(
  purchase: DownloadPurchaseLike | null | undefined,
  eventId: string,
  now: number = Date.now(),
): DownloadAccessResult {
  if (!purchase) return { ok: false, reason: 'not_found' };
  if (!purchase.eventId || purchase.eventId !== eventId) return { ok: false, reason: 'wrong_event' };
  if (purchase.status !== 'completed') return { ok: false, reason: 'not_completed' };
  if (isExpired(purchase, now)) return { ok: false, reason: 'expired' };
  return { ok: true };
}
